<template>
  <div class="dashboard">
    <div class="page-header">
      <h3>系统概览</h3>
      <div class="header-actions">
        <el-button 
          type="primary" 
          icon="el-icon-refresh" 
          @click="refreshData"
          :loading="loading"
        >
          刷新数据
        </el-button>
      </div>
    </div>

    <!-- 主要内容 -->
    <div class="dashboard-content">
      <!-- 数据加载状态 -->
      <div v-if="loading" class="loading-container">
        <el-skeleton :rows="8" animated />
      </div>
      
      <!-- 核心指标卡片 -->
      <div v-show="!loading" class="metrics-grid">
        <div class="metric-card total-logs">
          <div class="metric-icon">
            <i class="el-icon-document"></i>
          </div>
          <div class="metric-content">
            <div class="metric-value">{{ formatNumber(statistics.total_logs) }}</div>
            <div class="metric-label">总操作日志</div>
            <div class="metric-trend">
              <span class="trend-text">今日: {{ formatNumber(statistics.today_logs) }}</span>
            </div>
          </div>
        </div>

        <div class="metric-card success-rate">
          <div class="metric-icon">
            <i class="el-icon-success"></i>
          </div>
          <div class="metric-content">
            <div class="metric-value">{{ statistics.success_rate.toFixed(1) }}%</div>
            <div class="metric-label">成功率</div>
            <div class="metric-trend">
              <span class="trend-text">成功: {{ formatNumber(statistics.success_logs) }}</span>
            </div>
          </div>
        </div>

        <div class="metric-card total-users">
          <div class="metric-icon">
            <i class="el-icon-user"></i>
          </div>
          <div class="metric-content">
            <div class="metric-value">{{ formatNumber(statistics.total_users) }}</div>
            <div class="metric-label">总用户数</div>
            <div class="metric-trend">
              <span class="trend-text">活跃: {{ formatNumber(statistics.active_users) }}</span>
            </div>
          </div>
        </div>

        <div class="metric-card total-robots">
          <div class="metric-icon">
            <i class="el-icon-cpu"></i>
          </div>
          <div class="metric-content">
            <div class="metric-value">{{ formatNumber(statistics.total_robots) }}</div>
            <div class="metric-label">总机器人数</div>
            <div class="metric-trend">
              <span class="trend-text">本周: {{ formatNumber(statistics.week_logs) }}</span>
            </div>
          </div>
        </div>

        <div class="metric-card robot-online">
          <div class="metric-icon">
            <i class="el-icon-connection"></i>
          </div>
          <div class="metric-content">
            <div class="metric-value">{{ statistics.robot_online_stats.online_rate.toFixed(1) }}%</div>
            <div class="metric-label">机器人在线率</div>
            <div class="metric-trend">
              <span class="trend-text">在线: {{ formatNumber(statistics.robot_online_stats.online_count) }} / 离线: {{ formatNumber(statistics.robot_online_stats.offline_count) }}</span>
            </div>
          </div>
        </div>

        <div class="metric-card user-retention">
          <div class="metric-icon">
            <i class="el-icon-user-solid"></i>
          </div>
          <div class="metric-content">
            <div class="metric-value">{{ statistics.user_retention_rate.toFixed(1) }}%</div>
            <div class="metric-label">用户留存率</div>
            <div class="metric-trend">
              <span class="trend-text">活跃: {{ formatNumber(statistics.active_users) }} / 总数: {{ formatNumber(statistics.total_users) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 定时任务统计卡片 -->
      <div v-show="!loading" class="schedule-metrics-section">
        <div class="section-title">
          <i class="el-icon-timer"></i>
          <h3>定时任务执行监控</h3>
          <span class="subtitle">24小时内定时任务执行情况统计</span>
        </div>
        <div class="schedule-metrics-grid">
          <div class="metric-card schedule-total">
            <div class="metric-icon">
              <i class="el-icon-data-line"></i>
            </div>
            <div class="metric-content">
              <div class="metric-value">{{ formatNumber(statistics.schedule_summary.total_executions) }}</div>
              <div class="metric-label">总执行次数</div>
              <div class="metric-trend">
                <span class="trend-text">近24小时</span>
              </div>
            </div>
          </div>

          <div class="metric-card schedule-success">
            <div class="metric-icon">
              <i class="el-icon-circle-check"></i>
            </div>
            <div class="metric-content">
              <div class="metric-value">{{ formatNumber(statistics.schedule_summary.success_count) }}</div>
              <div class="metric-label">成功执行</div>
              <div class="metric-trend">
                <span class="trend-text">成功率: {{ statistics.schedule_summary.success_rate.toFixed(1) }}%</span>
              </div>
            </div>
          </div>

          <div class="metric-card schedule-failed">
            <div class="metric-icon">
              <i class="el-icon-circle-close"></i>
            </div>
            <div class="metric-content">
              <div class="metric-value">{{ formatNumber(statistics.schedule_summary.failed_count) }}</div>
              <div class="metric-label">失败次数</div>
              <div class="metric-trend">
                <span class="trend-text">需要关注</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 实时操作监控 -->
      <div v-show="!loading" class="realtime-metrics-section">
        <div class="section-title">
          <i class="el-icon-time"></i>
          <h3>实时操作监控</h3>
          <span class="subtitle">实时统计最近操作情况</span>
        </div>
        <div class="realtime-metrics-grid">
          <div class="metric-card realtime-1min">
            <div class="metric-icon">
              <i class="el-icon-timer"></i>
            </div>
            <div class="metric-content">
              <div class="metric-value">{{ formatNumber(statistics.recent_operation_count.last_1_minute) }}</div>
              <div class="metric-label">最近1分钟</div>
              <div class="metric-trend">
                <span class="trend-text">实时操作数</span>
              </div>
            </div>
          </div>

          <div class="metric-card realtime-5min">
            <div class="metric-icon">
              <i class="el-icon-time"></i>
            </div>
            <div class="metric-content">
              <div class="metric-value">{{ formatNumber(statistics.recent_operation_count.last_5_minutes) }}</div>
              <div class="metric-label">最近5分钟</div>
              <div class="metric-trend">
                <span class="trend-text">累计操作数</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 图表区域 -->
      <div v-show="!loading" class="charts-grid">
        <!-- 24小时活动趋势 -->
        <div class="chart-card">
          <div class="chart-header">
            <h4>24小时活动趋势</h4>
            <div class="chart-legend">
              <span class="legend-item">
                <span class="legend-color" style="background: #409EFF;"></span>
                操作次数
              </span>
            </div>
          </div>
          <div class="chart-content">
            <div ref="hourlyChart" class="chart-container"></div>
          </div>
        </div>

        <!-- 操作类型分布 -->
        <div class="chart-card">
          <div class="chart-header">
            <h4>热门操作类型</h4>
          </div>
          <div class="chart-content">
            <div ref="actionChart" class="chart-container"></div>
          </div>
        </div>

        <!-- 设备类型分布 -->
        <div class="chart-card">
          <div class="chart-header">
            <h4>设备类型分布</h4>
          </div>
          <div class="chart-content">
            <div ref="deviceChart" class="chart-container"></div>
          </div>
        </div>

        <!-- 执行结果统计 -->
        <div class="chart-card">
          <div class="chart-header">
            <h4>执行结果统计</h4>
          </div>
          <div class="chart-content">
            <div ref="resultChart" class="chart-container"></div>
          </div>
        </div>

        <!-- 定时任务24小时执行趋势 -->
        <div class="chart-card">
          <div class="chart-header">
            <h4>定时任务24小时执行趋势</h4>
            <div class="chart-legend">
              <span class="legend-item">
                <span class="legend-color" style="background: #67C23A;"></span>
                执行次数
              </span>
            </div>
          </div>
          <div class="chart-content">
            <div ref="scheduleHourlyChart" class="chart-container"></div>
          </div>
        </div>

        <!-- 峰值时段分析 -->
        <div class="chart-card">
          <div class="chart-header">
            <h4>峰值时段分析</h4>
            <div class="chart-legend">
              <span class="legend-item">
                <span class="legend-color" style="background: #E6A23C;"></span>
                操作次数（过去7天）
              </span>
            </div>
          </div>
          <div class="chart-content">
            <div ref="peakHoursChart" class="chart-container"></div>
          </div>
        </div>
      </div>

      <!-- 排行榜和最近日志 -->
      <div v-show="!loading" class="bottom-grid">
        <!-- 活跃用户排行 -->
        <div class="ranking-card">
          <div class="card-header">
            <h4>活跃用户排行</h4>
            <i class="el-icon-trophy"></i>
          </div>
          <div class="ranking-list">
            <div 
              v-for="(user, index) in statistics.top_users" 
              :key="`user-${index}-${user.username}`"
              class="ranking-item"
            >
              <div class="rank-number" :class="`rank-${index + 1}`">{{ index + 1 }}</div>
              <div class="user-info">
                <div class="username">{{ user.username }}</div>
                <div class="operation-count">{{ formatNumber(user.count) }} 次操作</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 热门机器人排行 -->
        <div class="ranking-card">
          <div class="card-header">
            <h4>热门机器人排行</h4>
            <i class="el-icon-cpu"></i>
          </div>
          <div class="ranking-list">
            <div 
              v-for="(robot, index) in statistics.top_robots" 
              :key="`robot-${index}-${robot.robot_name}`"
              class="ranking-item"
            >
              <div class="rank-number" :class="`rank-${index + 1}`">{{ index + 1 }}</div>
              <div class="robot-info">
                <div class="robot-name">{{ robot.robot_name }}</div>
                <div class="operation-count">{{ formatNumber(robot.count) }} 次操作</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 定时任务热门机器人 -->
        <div class="ranking-card schedule-robots">
          <div class="card-header">
            <h4>定时任务热门机器人</h4>
            <i class="el-icon-timer"></i>
          </div>
          <div class="ranking-list">
            <div 
              v-for="(robot, index) in statistics.top_schedule_robots" 
              :key="`schedule-robot-${index}-${robot.robot_id}`"
              class="ranking-item"
            >
              <div class="rank-number" :class="`rank-${index + 1}`">{{ index + 1 }}</div>
              <div class="robot-info">
                <div class="robot-name">{{ robot.robot_name }}</div>
                <div class="username-tag">用户: {{ robot.username }}</div>
                <div class="operation-count">{{ formatNumber(robot.count) }} 次执行</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 热门定时任务排行 -->
        <div class="ranking-card schedule-tasks">
          <div class="card-header">
            <h4>热门定时任务排行</h4>
            <i class="el-icon-finished"></i>
          </div>
          <div class="ranking-list">
            <div 
              v-for="(task, index) in statistics.top_schedule_tasks" 
              :key="`schedule-task-${index}-${task.schedule_id}`"
              class="ranking-item"
            >
              <div class="rank-number" :class="`rank-${index + 1}`">{{ index + 1 }}</div>
              <div class="task-info">
                <div class="task-name">{{ task.schedule_name }}</div>
                <div class="task-details">
                  <span class="detail-tag">任务ID: {{ task.schedule_id }}</span>
                  <span class="detail-tag">机器人: {{ task.robot_name }}</span>
                  <span class="detail-tag">用户: {{ task.username }}</span>
                  <span class="detail-tag">操作: {{ task.action }}</span>
                </div>
                <div class="operation-count">{{ formatNumber(task.count) }} 次执行</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 高频错误机器人排行 -->
        <div class="ranking-card error-robots">
          <div class="card-header">
            <h4>高频错误机器人排行</h4>
            <i class="el-icon-warning"></i>
          </div>
          <div class="ranking-list">
            <div 
              v-for="(robot, index) in statistics.top_error_robots" 
              :key="`error-robot-${index}-${robot.robot_id}`"
              class="ranking-item"
            >
              <div class="rank-number error-rank" :class="`rank-${index + 1}`">{{ index + 1 }}</div>
              <div class="robot-info">
                <div class="robot-name">{{ robot.robot_name }}</div>
                <div class="username-tag">用户: {{ robot.username }}</div>
                <div class="operation-count error-count">{{ formatNumber(robot.error_count) }} 次错误</div>
              </div>
            </div>
            <div v-if="statistics.top_error_robots.length === 0" class="empty-tip">
              暂无错误数据
            </div>
          </div>
        </div>

        <!-- 高频错误用户排行 -->
        <div class="ranking-card error-users">
          <div class="card-header">
            <h4>高频错误用户排行</h4>
            <i class="el-icon-warning-outline"></i>
          </div>
          <div class="ranking-list">
            <div 
              v-for="(user, index) in statistics.top_error_users" 
              :key="`error-user-${index}-${user.user_id}`"
              class="ranking-item"
            >
              <div class="rank-number error-rank" :class="`rank-${index + 1}`">{{ index + 1 }}</div>
              <div class="user-info">
                <div class="username">{{ user.username }}</div>
                <div class="operation-count error-count">{{ formatNumber(user.error_count) }} 次错误</div>
              </div>
            </div>
            <div v-if="statistics.top_error_users.length === 0" class="empty-tip">
              暂无错误数据
            </div>
          </div>
        </div>

        <!-- 最近操作日志 -->
        <div class="recent-logs-card">
          <div class="card-header">
            <h4>最近操作日志</h4>
            <el-button 
              type="text" 
              @click="$router.push('/admin/logs')"
              class="view-all-btn"
            >
              查看全部
            </el-button>
          </div>
          <div class="logs-list">
            <div 
              v-for="(log, index) in statistics.recent_logs" 
              :key="`log-${index}-${log.id}`"
              class="log-item"
            >
              <div class="log-status" :class="`status-${log.result}`">
                <i :class="getStatusIcon(log.result)"></i>
              </div>
              <div class="log-content">
                <div class="log-action">{{ log.action }}</div>
                <div class="log-details">
                  <span class="username">{{ log.username }}</span>
                  <span class="robot-name">{{ log.robot_name }}</span>
                  <span class="device-type">{{ log.device_type }}</span>
                </div>
                <div class="log-time">{{ formatTime(log.created_at) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getLogStatistics } from '@/api/log'
import * as echarts from 'echarts'

export default {
  name: 'DashboardPage',
  data() {
    return {
      loading: false,
      statistics: {
        total_logs: 0,
        success_logs: 0,
        failed_logs: 0,
        timeout_logs: 0,
        today_logs: 0,
        week_logs: 0,
        month_logs: 0,
        success_rate: 0,
        total_users: 0,
        total_robots: 0,
        active_users: 0,
        top_actions: [],
        top_users: [],
        top_robots: [],
        hourly_stats: [],
        device_stats: [],
        recent_logs: [],
        schedule_summary: {
          total_executions: 0,
          success_count: 0,
          failed_count: 0,
          success_rate: 0
        },
        schedule_hourly_stats: [],
        top_schedule_robots: [],
        top_schedule_tasks: [],
        robot_online_stats: {
          online_count: 0,
          offline_count: 0,
          online_rate: 0
        },
        user_retention_rate: 0,
        top_error_robots: [],
        top_error_users: [],
        recent_operation_count: {
          last_1_minute: 0,
          last_5_minutes: 0
        },
        peak_hours: []
      },
      charts: {
        hourly: null,
        action: null,
        device: null,
        result: null,
        scheduleHourly: null,
        peakHours: null
      },
      resizeTimer: null,
      initRetryCount: 0,
      maxRetryCount: 10,
      isInitializing: false
    }
  },
  mounted() {
    this.loadData()
    // 每5分钟自动刷新数据
    this.refreshTimer = setInterval(() => {
      this.loadData()
    }, 5 * 60 * 1000)
    
    // 监听窗口大小变化
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
    }
    // 移除窗口大小变化监听器
    window.removeEventListener('resize', this.handleResize)
    // 销毁图表实例
    Object.values(this.charts).forEach(chart => {
      if (chart) {
        chart.dispose()
      }
    })
  },
  methods: {
    getDefaultStatistics() {
      return {
        total_logs: 0,
        success_logs: 0,
        failed_logs: 0,
        timeout_logs: 0,
        today_logs: 0,
        week_logs: 0,
        month_logs: 0,
        success_rate: 0,
        total_users: 0,
        total_robots: 0,
        active_users: 0,
        top_actions: [],
        top_users: [],
        top_robots: [],
        hourly_stats: [],
        device_stats: [],
        recent_logs: [],
        schedule_summary: {
          total_executions: 0,
          success_count: 0,
          failed_count: 0,
          success_rate: 0
        },
        schedule_hourly_stats: [],
        top_schedule_robots: [],
        top_schedule_tasks: [],
        robot_online_stats: {
          online_count: 0,
          offline_count: 0,
          online_rate: 0
        },
        user_retention_rate: 0,
        top_error_robots: [],
        top_error_users: [],
        recent_operation_count: {
          last_1_minute: 0,
          last_5_minutes: 0
        },
        peak_hours: []
      }
    },
    async loadData() {
      this.loading = true
      this.initRetryCount = 0 // 重置重试计数
      this.isInitializing = false // 重置初始化标志
      try {
        const response = await getLogStatistics()
        // 合并默认值和API返回的数据，确保所有数组字段都有默认值
        const defaultStats = this.getDefaultStatistics()
        this.statistics = {
          ...defaultStats,
          ...response.data,
          // 确保嵌套对象也有默认值
          schedule_summary: {
            ...defaultStats.schedule_summary,
            ...(response.data.schedule_summary || {})
          },
          robot_online_stats: {
            ...defaultStats.robot_online_stats,
            ...(response.data.robot_online_stats || {})
          },
          recent_operation_count: {
            ...defaultStats.recent_operation_count,
            ...(response.data.recent_operation_count || {})
          },
          // 确保数组字段不为null
          top_actions: response.data.top_actions || [],
          top_users: response.data.top_users || [],
          top_robots: response.data.top_robots || [],
          hourly_stats: response.data.hourly_stats || [],
          device_stats: response.data.device_stats || [],
          recent_logs: response.data.recent_logs || [],
          schedule_hourly_stats: response.data.schedule_hourly_stats || [],
          top_schedule_robots: response.data.top_schedule_robots || [],
          top_schedule_tasks: response.data.top_schedule_tasks || [],
          top_error_robots: response.data.top_error_robots || [],
          top_error_users: response.data.top_error_users || [],
          peak_hours: response.data.peak_hours || []
        }
        // 等待DOM更新后再初始化图表
        await this.$nextTick()
        // 使用更长的延迟确保DOM完全渲染
        setTimeout(() => {
          this.initCharts()
        }, 300)
      } catch (error) {
        console.error('加载数据失败:', error)
        // 出错时使用默认值
        this.statistics = this.getDefaultStatistics()
      } finally {
        this.loading = false
      }
    },
    refreshData() {
      this.loadData()
    },
    handleResize() {
      // 防抖处理窗口大小变化
      clearTimeout(this.resizeTimer)
      this.resizeTimer = setTimeout(() => {
        Object.values(this.charts).forEach(chart => {
          if (chart) {
            chart.resize()
          }
        })
      }, 300)
    },
    initCharts() {
      // 防止重复初始化
      if (this.isInitializing) {
        console.log('图表正在初始化中，跳过重复调用')
        return
      }
      
      // 检查DOM元素是否存在
      if (!this.$refs.hourlyChart || !this.$refs.actionChart || 
          !this.$refs.deviceChart || !this.$refs.resultChart || 
          !this.$refs.scheduleHourlyChart || !this.$refs.peakHoursChart) {
        console.warn('图表容器DOM元素未准备好，跳过初始化')
        return
      }
      
      // 检查元素是否可见（宽度和高度大于0）
      const elements = [
        this.$refs.hourlyChart,
        this.$refs.actionChart,
        this.$refs.deviceChart,
        this.$refs.resultChart,
        this.$refs.scheduleHourlyChart,
        this.$refs.peakHoursChart
      ]
      
      const allVisible = elements.every(el => {
        const rect = el.getBoundingClientRect()
        return rect.width > 0 && rect.height > 0
      })
      
      if (!allVisible) {
        console.warn('图表容器不可见，跳过初始化')
        return
      }
      
      // 设置初始化标志
      this.isInitializing = true
      
      try {
        this.initHourlyChart()
        this.initActionChart()
        this.initDeviceChart()
        this.initResultChart()
        this.initScheduleHourlyChart()
        this.initPeakHoursChart()
      } catch (error) {
        console.error('图表初始化失败:', error)
      } finally {
        // 重置初始化标志
        this.isInitializing = false
      }
    },
    initHourlyChart() {
      if (!this.$refs.hourlyChart) {
        console.warn('hourlyChart DOM元素不存在')
        return
      }
      
      if (this.charts.hourly) {
        this.charts.hourly.dispose()
      }
      this.charts.hourly = echarts.init(this.$refs.hourlyChart)
      
      // 确保 hourly_stats 是数组
      const hourlyStats = this.statistics.hourly_stats || []
      
      const option = {
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          borderColor: '#409EFF',
          textStyle: { color: '#fff' }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: hourlyStats.map(item => item.hour),
          axisLine: { lineStyle: { color: '#e0e0e0' } },
          axisLabel: { color: '#666' }
        },
        yAxis: {
          type: 'value',
          axisLine: { lineStyle: { color: '#e0e0e0' } },
          axisLabel: { color: '#666' },
          splitLine: { lineStyle: { color: '#f0f0f0' } }
        },
        series: [{
          data: hourlyStats.map(item => item.count),
          type: 'line',
          smooth: true,
          lineStyle: { color: '#409EFF', width: 3 },
          itemStyle: { color: '#409EFF' },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(64, 158, 255, 0.3)' },
                { offset: 1, color: 'rgba(64, 158, 255, 0.05)' }
              ]
            }
          }
        }]
      }
      this.charts.hourly.setOption(option)
    },
    initActionChart() {
      if (!this.$refs.actionChart) {
        console.warn('actionChart DOM元素不存在')
        return
      }
      
      if (this.charts.action) {
        this.charts.action.dispose()
      }
      this.charts.action = echarts.init(this.$refs.actionChart)
      
      // 确保 top_actions 是数组
      const topActions = this.statistics.top_actions || []
      
      const option = {
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          borderColor: '#409EFF',
          textStyle: { color: '#fff' }
        },
        series: [{
          type: 'pie',
          radius: ['40%', '70%'],
          data: topActions.map(item => ({
            value: item.count,
            name: item.action
          })),
          itemStyle: {
            borderRadius: 8,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: true,
            formatter: '{b}: {c}'
          }
        }]
      }
      this.charts.action.setOption(option)
    },
    initDeviceChart() {
      if (!this.$refs.deviceChart) {
        console.warn('deviceChart DOM元素不存在')
        return
      }
      
      if (this.charts.device) {
        this.charts.device.dispose()
      }
      this.charts.device = echarts.init(this.$refs.deviceChart)
      
      // 确保 device_stats 是数组
      const deviceStats = this.statistics.device_stats || []
      
      const option = {
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          borderColor: '#409EFF',
          textStyle: { color: '#fff' }
        },
        series: [{
          type: 'pie',
          radius: '70%',
          data: deviceStats.map(item => ({
            value: item.count,
            name: item.device_type
          })),
          itemStyle: {
            borderRadius: 8,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: true,
            formatter: '{b}: {c}'
          }
        }]
      }
      this.charts.device.setOption(option)
    },
    initResultChart() {
      if (!this.$refs.resultChart) {
        console.warn('resultChart DOM元素不存在')
        return
      }
      
      if (this.charts.result) {
        this.charts.result.dispose()
      }
      this.charts.result = echarts.init(this.$refs.resultChart)
      
      const resultData = [
        { value: this.statistics.success_logs, name: '成功', itemStyle: { color: '#67C23A' } },
        { value: this.statistics.failed_logs, name: '失败', itemStyle: { color: '#F56C6C' } },
        { value: this.statistics.timeout_logs, name: '超时', itemStyle: { color: '#E6A23C' } }
      ]
      
      const option = {
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          borderColor: '#409EFF',
          textStyle: { color: '#fff' }
        },
        series: [{
          type: 'pie',
          radius: '70%',
          data: resultData,
          itemStyle: {
            borderRadius: 8,
            borderColor: '#fff',
            borderWidth: 2
          },
          label: {
            show: true,
            formatter: '{b}: {c}'
          }
        }]
      }
      this.charts.result.setOption(option)
    },
    initScheduleHourlyChart() {
      if (!this.$refs.scheduleHourlyChart) {
        console.warn('scheduleHourlyChart DOM元素不存在')
        return
      }
      
      if (this.charts.scheduleHourly) {
        this.charts.scheduleHourly.dispose()
      }
      this.charts.scheduleHourly = echarts.init(this.$refs.scheduleHourlyChart)
      
      // 确保 schedule_hourly_stats 是数组
      const scheduleHourlyStats = this.statistics.schedule_hourly_stats || []
      
      const option = {
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          borderColor: '#67C23A',
          textStyle: { color: '#fff' }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: scheduleHourlyStats.map(item => item.hour),
          axisLine: { lineStyle: { color: '#e0e0e0' } },
          axisLabel: { color: '#666' }
        },
        yAxis: {
          type: 'value',
          axisLine: { lineStyle: { color: '#e0e0e0' } },
          axisLabel: { color: '#666' },
          splitLine: { lineStyle: { color: '#f0f0f0' } }
        },
        series: [{
          data: scheduleHourlyStats.map(item => item.count),
          type: 'line',
          smooth: true,
          lineStyle: { color: '#67C23A', width: 3 },
          itemStyle: { color: '#67C23A' },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(103, 194, 58, 0.3)' },
                { offset: 1, color: 'rgba(103, 194, 58, 0.05)' }
              ]
            }
          }
        }]
      }
      this.charts.scheduleHourly.setOption(option)
    },
    initPeakHoursChart() {
      if (!this.$refs.peakHoursChart) {
        console.warn('peakHoursChart DOM元素不存在')
        return
      }
      
      if (this.charts.peakHours) {
        this.charts.peakHours.dispose()
      }
      this.charts.peakHours = echarts.init(this.$refs.peakHoursChart)
      
      // 确保 peak_hours 是数组
      const peakHours = this.statistics.peak_hours || []
      
      const option = {
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          borderColor: '#E6A23C',
          textStyle: { color: '#fff' }
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: peakHours.map(item => item.hour),
          axisLine: { lineStyle: { color: '#e0e0e0' } },
          axisLabel: { 
            color: '#666',
            rotate: 45
          }
        },
        yAxis: {
          type: 'value',
          axisLine: { lineStyle: { color: '#e0e0e0' } },
          axisLabel: { color: '#666' },
          splitLine: { lineStyle: { color: '#f0f0f0' } }
        },
        series: [{
          data: peakHours.map(item => item.count),
          type: 'bar',
          itemStyle: {
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: '#E6A23C' },
                { offset: 1, color: '#F5C842' }
              ]
            },
            borderRadius: [4, 4, 0, 0]
          },
          label: {
            show: true,
            position: 'top',
            color: '#666'
          }
        }]
      }
      this.charts.peakHours.setOption(option)
    },
    formatNumber(num) {
      if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M'
      } else if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K'
      }
      return num.toString()
    },
    formatTime(timeStr) {
      const date = new Date(timeStr)
      const now = new Date()
      const diff = now - date
      
      if (diff < 60000) { // 1分钟内
        return '刚刚'
      } else if (diff < 3600000) { // 1小时内
        return Math.floor(diff / 60000) + '分钟前'
      } else if (diff < 86400000) { // 1天内
        return Math.floor(diff / 3600000) + '小时前'
      } else {
        return date.toLocaleDateString()
      }
    },
    getStatusIcon(result) {
      switch (result) {
        case 'success':
          return 'el-icon-success'
        case 'failed':
          return 'el-icon-error'
        case 'timeout':
          return 'el-icon-warning'
        default:
          return 'el-icon-info'
      }
    }
  }
}
</script>

<style scoped>
.dashboard {
  min-height: 100%;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  background: rgba(255, 255, 255, 0.9);
  padding: 20px 30px;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
}

.page-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 28px;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.loading-container {
  background: rgba(255, 255, 255, 0.9);
  padding: 30px;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
}

.dashboard-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* 定时任务统计部分 */
.schedule-metrics-section {
  margin-bottom: 24px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding: 16px 24px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.section-title i {
  font-size: 28px;
  color: #67C23A;
}

.section-title h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #2c3e50;
  background: linear-gradient(135deg, #67C23A 0%, #85ce61 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.section-title .subtitle {
  margin-left: auto;
  font-size: 13px;
  color: #909399;
  font-weight: 500;
}

.schedule-metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

/* 实时操作监控部分 */
.realtime-metrics-section {
  margin-bottom: 24px;
}

.realtime-metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

.metric-card.realtime-1min::before {
  background: linear-gradient(90deg, #FF9800 0%, #FFB74D 100%);
}

.metric-card.realtime-5min::before {
  background: linear-gradient(90deg, #FF5722 0%, #FF8A65 100%);
}

.metric-card.realtime-1min .metric-icon {
  background: linear-gradient(135deg, #FF9800 0%, #FFB74D 100%);
  box-shadow: 0 4px 16px rgba(255, 152, 0, 0.4);
}

.metric-card.realtime-5min .metric-icon {
  background: linear-gradient(135deg, #FF5722 0%, #FF8A65 100%);
  box-shadow: 0 4px 16px rgba(255, 87, 34, 0.4);
}

.metric-card.schedule-total::before {
  background: linear-gradient(90deg, #67C23A 0%, #85ce61 100%);
}

.metric-card.schedule-success::before {
  background: linear-gradient(90deg, #409EFF 0%, #66b1ff 100%);
}

.metric-card.schedule-failed::before {
  background: linear-gradient(90deg, #F56C6C 0%, #f78989 100%);
}

.metric-card.schedule-total .metric-icon {
  background: linear-gradient(135deg, #67C23A 0%, #85ce61 100%);
  box-shadow: 0 4px 16px rgba(103, 194, 58, 0.4);
}

.metric-card.schedule-success .metric-icon {
  background: linear-gradient(135deg, #409EFF 0%, #66b1ff 100%);
  box-shadow: 0 4px 16px rgba(64, 158, 255, 0.4);
}

.metric-card.schedule-failed .metric-icon {
  background: linear-gradient(135deg, #F56C6C 0%, #f78989 100%);
  box-shadow: 0 4px 16px rgba(245, 108, 108, 0.4);
}

/* 核心指标卡片 */
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
}

.metric-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.metric-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
}

.metric-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.15);
}

.metric-card.total-logs::before {
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
}

.metric-card.success-rate::before {
  background: linear-gradient(90deg, #56ab2f 0%, #a8e6cf 100%);
}

.metric-card.total-users::before {
  background: linear-gradient(90deg, #ff6b6b 0%, #feca57 100%);
}

.metric-card.total-robots::before {
  background: linear-gradient(90deg, #4ecdc4 0%, #44a08d 100%);
}

.metric-card.robot-online::before {
  background: linear-gradient(90deg, #409EFF 0%, #66b1ff 100%);
}

.metric-card.user-retention::before {
  background: linear-gradient(90deg, #9C27B0 0%, #BA68C8 100%);
}

.metric-card.robot-online .metric-icon {
  background: linear-gradient(135deg, #409EFF 0%, #66b1ff 100%);
  box-shadow: 0 4px 16px rgba(64, 158, 255, 0.4);
}

.metric-card.user-retention .metric-icon {
  background: linear-gradient(135deg, #9C27B0 0%, #BA68C8 100%);
  box-shadow: 0 4px 16px rgba(156, 39, 176, 0.4);
}

.metric-card {
  display: flex;
  align-items: center;
  gap: 20px;
}

.metric-icon {
  width: 60px;
  height: 60px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  color: white;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.4);
}

.metric-card.success-rate .metric-icon {
  background: linear-gradient(135deg, #56ab2f 0%, #a8e6cf 100%);
  box-shadow: 0 4px 16px rgba(86, 171, 47, 0.4);
}

.metric-card.total-users .metric-icon {
  background: linear-gradient(135deg, #ff6b6b 0%, #feca57 100%);
  box-shadow: 0 4px 16px rgba(255, 107, 107, 0.4);
}

.metric-card.total-robots .metric-icon {
  background: linear-gradient(135deg, #4ecdc4 0%, #44a08d 100%);
  box-shadow: 0 4px 16px rgba(78, 205, 196, 0.4);
}

.metric-content {
  flex: 1;
}

.metric-value {
  font-size: 32px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 4px;
  line-height: 1;
}

.metric-label {
  font-size: 14px;
  color: #7f8c8d;
  margin-bottom: 8px;
  font-weight: 500;
}

.metric-trend {
  font-size: 12px;
  color: #95a5a6;
}

.trend-text {
  background: rgba(52, 152, 219, 0.1);
  padding: 4px 8px;
  border-radius: 8px;
  color: #3498db;
  font-weight: 500;
}

/* 图表区域 */
.charts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
}

.chart-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
}

.chart-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f8f9fa;
}

.chart-header h4 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
}

.chart-legend {
  display: flex;
  gap: 16px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #7f8c8d;
}

.legend-color {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.chart-content {
  height: 300px;
}

.chart-container {
  width: 100%;
  height: 100%;
}

/* 底部网格 */
.bottom-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 20px;
}

.ranking-card,
.recent-logs-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
}

.ranking-card:hover,
.recent-logs-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f8f9fa;
}

.card-header h4 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
}

.card-header i {
  font-size: 20px;
  color: #f39c12;
}

.view-all-btn {
  color: #3498db !important;
  font-weight: 500;
}

.ranking-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ranking-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px;
  border-radius: 12px;
  background: rgba(248, 249, 250, 0.8);
  transition: all 0.3s ease;
}

.ranking-item:hover {
  background: rgba(52, 152, 219, 0.1);
  transform: translateX(4px);
}

.rank-number {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  color: white;
  background: #95a5a6;
}

.rank-number.rank-1 {
  background: linear-gradient(135deg, #f39c12 0%, #e67e22 100%);
  box-shadow: 0 4px 12px rgba(243, 156, 18, 0.4);
}

.rank-number.rank-2 {
  background: linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%);
  box-shadow: 0 4px 12px rgba(149, 165, 166, 0.4);
}

.rank-number.rank-3 {
  background: linear-gradient(135deg, #e67e22 0%, #d35400 100%);
  box-shadow: 0 4px 12px rgba(230, 126, 34, 0.4);
}

.user-info,
.robot-info {
  flex: 1;
}

.username,
.robot-name {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
}

.operation-count {
  font-size: 12px;
  color: #7f8c8d;
}

/* 定时任务排行榜样式 */
.ranking-card.schedule-robots .card-header i,
.ranking-card.schedule-tasks .card-header i {
  color: #67C23A;
}

.username-tag {
  font-size: 11px;
  color: #909399;
  margin-bottom: 2px;
}

.task-info {
  flex: 1;
}

.task-name {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.task-id {
  font-size: 12px;
  font-weight: 700;
  color: #67C23A;
  background: rgba(103, 194, 58, 0.1);
  padding: 2px 8px;
  border-radius: 6px;
  font-family: 'Courier New', monospace;
}

.task-details {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 4px;
}

.detail-tag {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 6px;
  background: rgba(103, 194, 58, 0.1);
  color: #67C23A;
  font-weight: 500;
}

/* 错误排行样式 */
.ranking-card.error-robots .card-header i,
.ranking-card.error-users .card-header i {
  color: #F56C6C;
}

.rank-number.error-rank {
  background: #F56C6C;
}

.rank-number.error-rank.rank-1 {
  background: linear-gradient(135deg, #F56C6C 0%, #F78989 100%);
  box-shadow: 0 4px 12px rgba(245, 108, 108, 0.4);
}

.rank-number.error-rank.rank-2 {
  background: linear-gradient(135deg, #F78989 0%, #F9A5A5 100%);
  box-shadow: 0 4px 12px rgba(247, 137, 137, 0.4);
}

.rank-number.error-rank.rank-3 {
  background: linear-gradient(135deg, #F9A5A5 0%, #FBC1C1 100%);
  box-shadow: 0 4px 12px rgba(249, 165, 165, 0.4);
}

.operation-count.error-count {
  color: #F56C6C;
  font-weight: 600;
}

.empty-tip {
  text-align: center;
  padding: 20px;
  color: #909399;
  font-size: 14px;
}

/* 最近日志 */
.logs-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 400px;
  overflow-y: auto;
}

.log-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  border-radius: 12px;
  background: rgba(248, 249, 250, 0.8);
  transition: all 0.3s ease;
}

.log-item:hover {
  background: rgba(52, 152, 219, 0.1);
  transform: translateX(4px);
}

.log-status {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: white;
  flex-shrink: 0;
}

.log-status.status-success {
  background: linear-gradient(135deg, #27ae60 0%, #2ecc71 100%);
  box-shadow: 0 4px 12px rgba(39, 174, 96, 0.4);
}

.log-status.status-failed {
  background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
  box-shadow: 0 4px 12px rgba(231, 76, 60, 0.4);
}

.log-status.status-timeout {
  background: linear-gradient(135deg, #f39c12 0%, #e67e22 100%);
  box-shadow: 0 4px 12px rgba(243, 156, 18, 0.4);
}

.log-content {
  flex: 1;
  min-width: 0;
}

.log-action {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
  font-size: 14px;
}

.log-details {
  display: flex;
  gap: 8px;
  margin-bottom: 4px;
  flex-wrap: wrap;
}

.log-details span {
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 6px;
  background: rgba(52, 152, 219, 0.1);
  color: #3498db;
  font-weight: 500;
}

.log-time {
  font-size: 11px;
  color: #95a5a6;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .charts-grid {
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  }
  
  .bottom-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .dashboard {
    padding: 16px;
  }
  
  .page-header {
    flex-direction: column;
    gap: 16px;
    text-align: center;
    padding: 20px;
  }
  
  .page-header h3 {
    font-size: 24px;
  }
  
  .metrics-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .metric-card {
    padding: 20px;
  }
  
  .metric-value {
    font-size: 28px;
  }
  
  .charts-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .chart-card {
    padding: 20px;
  }
  
  .chart-content {
    height: 250px;
  }
  
  .bottom-grid {
    gap: 16px;
  }
  
  .ranking-card,
  .recent-logs-card {
    padding: 20px;
  }
}

@media (max-width: 480px) {
  .dashboard {
    padding: 12px;
  }
  
  .page-header {
    padding: 16px;
  }
  
  .page-header h3 {
    font-size: 20px;
  }
  
  .metric-card {
    padding: 16px;
    flex-direction: column;
    text-align: center;
    gap: 16px;
  }
  
  .metric-icon {
    width: 50px;
    height: 50px;
    font-size: 24px;
  }
  
  .metric-value {
    font-size: 24px;
  }
  
  .chart-card {
    padding: 16px;
  }
  
  .chart-content {
    height: 200px;
  }
  
  .ranking-card,
  .recent-logs-card {
    padding: 16px;
  }
  
  .log-details {
    flex-direction: column;
    gap: 4px;
  }
}

/* 滚动条样式 */
.logs-list::-webkit-scrollbar {
  width: 6px;
}

.logs-list::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 3px;
}

.logs-list::-webkit-scrollbar-thumb {
  background: rgba(52, 152, 219, 0.6);
  border-radius: 3px;
}

.logs-list::-webkit-scrollbar-thumb:hover {
  background: rgba(52, 152, 219, 0.8);
}

/* 动画效果 */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.metric-card,
.chart-card,
.ranking-card,
.recent-logs-card {
  animation: fadeInUp 0.6s ease-out;
}

.metric-card:nth-child(1) { animation-delay: 0.1s; }
.metric-card:nth-child(2) { animation-delay: 0.2s; }
.metric-card:nth-child(3) { animation-delay: 0.3s; }
.metric-card:nth-child(4) { animation-delay: 0.4s; }
</style>
